import { SAVINGS_PRESET_IMAGE_UPLOAD_TYPE } from 'app/constants/fileUploads';

import FileProcessingApiHandler from 'app/services/fileProcessingApiHandler';

import {
  UploadResponsePayload,
  VerifyUploadResponsePayload,
} from 'app/types/fileProcessingService';

export const ALLOWED_SAVINGS_PRESET_IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg'];

// Maximum accepted upload size per preset image (5 MB), matching the file-processing-service
// convention for other asset types.
export const MAX_SAVINGS_PRESET_IMAGE_BYTES = 5 * 1024 * 1024;

/**
 * Returns whether the given file is an allowed savings preset cover image (png / jpg / jpeg),
 * based on its file extension. The backend independently enforces the real image check on verify.
 */
export const isAllowedSavingsPresetImage = (file: File): boolean => {
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  return ALLOWED_SAVINGS_PRESET_IMAGE_EXTENSIONS.includes(extension);
};

/**
 * Returns whether the given file is within the accepted savings preset image size limit (5 MB).
 */
export const isWithinSavingsPresetImageSize = (file: File): boolean =>
  file.size <= MAX_SAVINGS_PRESET_IMAGE_BYTES;

/**
 * Uploads a global savings preset cover image through the file-processing-service direct-upload
 * flow and returns the verified file upload id.
 *
 * The flow is: request a presigned URL for the savings-preset type with no company scope, PUT the
 * file straight to storage, then verify the upload so it transitions to SUCCEEDED and becomes
 * available in the catalogue. Presets are global, so no companyId is sent.
 */
export const uploadSavingsPresetImage = async (file: File): Promise<string> => {
  const presign = (await FileProcessingApiHandler.getUploadPreSignedUrl(
    file.name,
    undefined,
    SAVINGS_PRESET_IMAGE_UPLOAD_TYPE
  )) as UploadResponsePayload;

  if (!presign.id) {
    throw new Error(
      'File processing service did not return an upload id for the savings preset image.'
    );
  }

  const response = await fetch(presign.url, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type },
  });

  if (!response.ok) {
    throw new Error(`Failed to upload savings preset image: ${response.statusText}`);
  }

  const verified = (await FileProcessingApiHandler.verifyUpload(
    presign.id
  )) as VerifyUploadResponsePayload;

  if (verified.status !== 'SUCCEEDED') {
    throw new Error('The uploaded savings preset image failed verification.');
  }

  return verified.id;
};
